import React from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorPanel } from '~/components/features/ErrorPanel'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { useCurrentBrandAccountSurveyGroupsQueryData } from '~/hooks/useCurrentBrandAccountSurveyGroupsQuery'
import { compile } from '~/routes/paths'
import { BrandAccountOp, exists } from '~/utils'

type CheckExists = 'exists'
type CheckHasReadiedSurveyGroups = 'hasReadiedSurveyGroups'

type Check = CheckExists | CheckHasReadiedSurveyGroups

const isCheckExists = (check: Check): check is CheckExists => check === 'exists'
const isCheckHasReadiedSurveyGroups = (
  check: Check,
): check is CheckHasReadiedSurveyGroups => check === 'hasReadiedSurveyGroups'

type CheckCurrentBrandAccountOptions = {
  backLinkPath?: string
  checks?: Check[]
}

const defaultBackLinkPath = compile('top', {})
const defaultChecks: Check[] = ['exists', 'hasReadiedSurveyGroups']

export const checkCurrentBrandAccount = <Props extends {}>(
  WrappedComponent: React.ComponentType<Props>,
  {
    backLinkPath = defaultBackLinkPath,
    checks = defaultChecks,
  }: CheckCurrentBrandAccountOptions = {},
) => {
  const CurrentBrandAccountChecker = (props: Props) => {
    const { t } = useTranslation()
    const currentBrandAccount = useCommonQueryCurrentBrandAccount()
    const currentBrandAccountSurveyGroups =
      useCurrentBrandAccountSurveyGroupsQueryData()

    const message = React.useMemo(() => {
      const checkExists = checks.find(isCheckExists)
      const checkHasReadiedSurveyGroups = checks.find(
        isCheckHasReadiedSurveyGroups,
      )

      if (exists(checkExists)) {
        if (!exists(currentBrandAccount)) {
          return t('brand_account_not_found_2')
        }
      }

      if (exists(checkHasReadiedSurveyGroups)) {
        if (!exists(currentBrandAccount)) {
          return t('brand_account_not_found_3')
        }

        if (
          !BrandAccountOp.hasReadiedSurveyGroups({
            ...currentBrandAccount,
            surveyGroups: currentBrandAccountSurveyGroups,
          })
        ) {
          return t('cannot_be_used_because_setup_is_not_complete_1')
        }
      }

      return null
    }, [currentBrandAccount, currentBrandAccountSurveyGroups, t])

    if (exists(message)) {
      return <ErrorPanel backLinkPath={backLinkPath} message={message} />
    }

    return <WrappedComponent {...props} />
  }

  return CurrentBrandAccountChecker
}
